// MP219 — Status badge / pill primitive.
//
// Consolidates ~62 inline patterns across apps/web where each page
// re-rolled its own colour-coded status pill (ActionStatusPill,
// CampaignStatusPill, OrgStatusPill, RolePill, etc). One component,
// six semantic tones, three sizes. Match this against the existing
// shadcn-derived primitives in this package (cva + cn).

import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/cn';

const statusBadgeVariants = cva(
  // Base: tabular-nums so numeric badges don't reflow; uppercase
  // tracking matches the design language already in use across status
  // pills in shell pages.
  'inline-flex items-center gap-1 rounded-full border font-semibold uppercase tabular-nums tracking-wider',
  {
    variants: {
      tone: {
        // Successful / live / active — emerald.
        success:
          'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-800/60 dark:bg-emerald-950/30 dark:text-emerald-300',
        // In-progress / pending / scheduled — amber.
        warning:
          'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800/60 dark:bg-amber-950/30 dark:text-amber-300',
        // Failed / blocked / quarantined — rose.
        danger:
          'border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-800/60 dark:bg-rose-950/30 dark:text-rose-300',
        // Informational / draft / queued — violet.
        info: 'border-violet-200 bg-violet-50 text-violet-800 dark:border-violet-800/60 dark:bg-violet-950/30 dark:text-violet-300',
        // Brand / "live"-ish secondary — primary tone.
        primary:
          'border-primary/30 bg-primary/10 text-primary dark:border-primary/40 dark:bg-primary/20',
        // Neutral / inactive / paused — muted greys.
        muted:
          'border-slate-200 bg-slate-100 text-slate-700 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-300',
      },
      size: {
        sm: 'px-1.5 py-0.5 text-[9px]',
        md: 'px-2 py-0.5 text-[10px]',
        lg: 'px-2.5 py-1 text-xs',
      },
    },
    defaultVariants: {
      tone: 'muted',
      size: 'md',
    },
  },
);

export interface StatusBadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof statusBadgeVariants> {
  /** Optional icon (lucide-react component, instantiated by caller).
   *  Rendered at h-3 w-3 inside the pill. */
  icon?: React.ReactNode;
}

export const StatusBadge = React.forwardRef<HTMLSpanElement, StatusBadgeProps>(
  ({ className, tone, size, icon, children, ...props }, ref) => {
    return (
      <span ref={ref} className={cn(statusBadgeVariants({ tone, size }), className)} {...props}>
        {icon}
        {children}
      </span>
    );
  },
);
StatusBadge.displayName = 'StatusBadge';

export { statusBadgeVariants };
